import { describePaymentMethod } from '@/components/payment-methods/PaymentMethodsList'
import { useJobillaOrgId } from '@/hooks'
import { apiErrorMessage, getStripe } from '@/lib'
import { Button, logger } from '@/ui'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import {
	type BillingProfileData,
	type PaymentMethodData,
	type PlanData,
	type PlanInterval,
	type SubscriptionCheckoutData,
	subscriptionCheckoutStatus,
	useSubscriptionStore,
} from '@jobilla/nexus-sdk'
import { upperFirst } from 'lodash'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPrice } from './format'

class AuthenticationError extends Error {}

interface ConfirmStepProps {
	plan: PlanData
	interval: PlanInterval
	profile: BillingProfileData
	card: PaymentMethodData
	onEditPlan: () => void
	onEditDetails: () => void
	onEditCard: () => void
	onSubscribed: () => void
}

export function ConfirmStep(
	{ plan, interval, profile, card, onEditPlan, onEditDetails, onEditCard, onSubscribed }: ConfirmStepProps,
) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const subscribeMutation = useSubscriptionStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const price = plan[interval]
	const address = profile.address

	async function authenticate(checkout: SubscriptionCheckoutData) {
		const stripe = checkout.clientSecret ? await getStripe(checkout.publishableKey) : null

		if (!stripe || !checkout.clientSecret) {
			throw new AuthenticationError(t('subscribe.confirm.authenticationUnavailable'))
		}

		const { error: stripeError } = await stripe.handleNextAction({ clientSecret: checkout.clientSecret })

		if (stripeError) {
			throw new AuthenticationError(stripeError.message ?? t('subscribe.confirm.authenticationFailed'))
		}
	}

	async function subscribe() {
		setIsSubmitting(true)
		setError(null)

		try {
			const checkout = await subscribeMutation.mutateAsync({
				organisation: organisationId,
				profile: profile.id,
				data: { tier: plan.tier, interval, paymentMethodId: card.id },
			})

			if (checkout.status === subscriptionCheckoutStatus.requires_action) {
				await authenticate(checkout)
			}

			logger.track('Subscription: subscribed in-app', {
				category: 'billing',
				data: { tier: plan.tier, interval },
			})
			onSubscribed()
		} catch (caught) {
			logger.track('Subscription: subscribe failed', {
				category: 'billing',
				data: {
					tier: plan.tier,
					interval,
					reason: caught instanceof AuthenticationError ? 'authentication' : 'checkout',
				},
			})
			if (caught instanceof AuthenticationError) {
				setError(caught.message)
			} else {
				setError(apiErrorMessage(caught) ?? t('subscribe.confirm.failed'))
				logger.error(caught, { category: 'billing', data: { action: 'subscribe', organisationId } })
			}
			setIsSubmitting(false)
		}
	}

	return (
		<div className="mx-auto max-w-lg space-y-6">
			<h1 className="text-2xl font-bold text-grey-900">{t('subscribe.confirm.title')}</h1>

			<div className="divide-y divide-grey-100 rounded-xl border border-grey-200 bg-white px-5">
				<SummaryRow
					label={t('subscribe.confirm.plan')}
					onEdit={onEditPlan}
					disabled={isSubmitting}
				>
					{upperFirst(plan.tier)} · {t(`subscribe.interval.${interval}`)}
				</SummaryRow>
				<SummaryRow
					label={t('subscribe.confirm.billedTo')}
					onEdit={onEditDetails}
					disabled={isSubmitting}
				>
					<span className="block">{profile.name}</span>
					<span className="block text-grey-500">
						{[address?.line1, address?.postalCode, address?.city, address?.country].filter(Boolean).join(
							', ',
						)}
					</span>
				</SummaryRow>
				<SummaryRow
					label={t('subscribe.confirm.card')}
					onEdit={onEditCard}
					disabled={isSubmitting}
				>
					{describePaymentMethod(card, t)}
				</SummaryRow>
				{price
					? (
						<div className="flex items-baseline justify-between py-4">
							<span className="text-sm text-grey-500">{t('subscribe.confirm.price')}</span>
							<span className="text-right">
								<span className="block text-lg font-bold text-grey-900">
									{formatPrice(price, i18n.language)}
								</span>
								<span className="block text-xs text-grey-400">{t('subscribe.plans.vatExcluded')}</span>
							</span>
						</div>
					)
					: null}
			</div>

			{error
				? (
					<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
						{error}
					</p>
				)
				: null}

			<Button
				type="committal"
				fullWidth={true}
				onClick={subscribe}
				isLoading={isSubmitting}
				disabled={isSubmitting}
			>
				{t('subscribe.confirm.subscribe', { plan: upperFirst(plan.tier) })}
			</Button>
			<p className="flex items-center justify-center gap-1.5 text-xs text-grey-500">
				<ShieldCheckIcon className="h-3.5 w-3.5" />
				{t('subscribe.confirm.secure')}
			</p>
		</div>
	)
}

function SummaryRow(
	{ label, onEdit, disabled, children }: {
		label: string
		onEdit: () => void
		disabled: boolean
		children: ReactNode
	},
) {
	const { t } = useTranslation('common')

	return (
		<div className="flex items-start justify-between gap-4 py-4">
			<div className="min-w-0">
				<span className="block text-xs font-semibold uppercase tracking-wide text-grey-500">{label}</span>
				<span className="mt-1 block text-sm text-grey-900">{children}</span>
			</div>
			<Button type="invisible" size="tiny" onClick={onEdit} disabled={disabled}>
				{t('subscribe.confirm.change')}
			</Button>
		</div>
	)
}
